import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import type { ProfileDetailsResponse } from '@/core/api/models/profile';
import type { User } from '@/core/stores/appStore';
import { logger } from '@/core/logger';
import { parsePositiveLoanAmountAed } from '@/modules/properties-web/services/mapUnitDetails';

export interface ChangePasswordRequest {
    email: string;
    current_password: string;
    new_password: string;
    new_password_confirm: string;
}

export interface ChangePasswordResponse {
    status: 'success';
    message: string;
}

export interface ChangePasswordError {
    error?: string;
    code?: string;
    message?: string;
    errors?: Array<{
        field: string;
        message: string;
        code: string;
    }>;
}

function trimOrUndefined(value: unknown): string | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

/** Trim a string; `null` / empty / whitespace / non-string become `null`. */
function assignOrClearStringField(
    patch: Partial<User>,
    key: keyof User,
    value: unknown,
): void {
    (patch as Record<string, string | null>)[key] = trimOrUndefined(value) ?? null;
}

const PROFILE_DETAIL_STRING_FIELDS = [
    'customer_id',
    'customer_number',
    'emairates_id',
    'passport_number',
    'primary_phone',
    'secondary_phone',
    'tertiary_phone',
    'primary_country_code',
    'secondary_country_code',
    'tertiary_country_code',
    'nationality',
    'registration_status',
    'date_of_birth',
    'appointmentBookingStartDate',
    'appointmentBookingEndDate',
] as const satisfies ReadonlyArray<keyof ProfileDetailsResponse & keyof User>;

/** Present email key updates the store; omitted email key is left untouched. */
const PRESENCE_SCOPED_EMAIL_FIELDS = [
    'primary_email',
    'secondary_email',
    'tertiary_email',
] as const satisfies ReadonlyArray<keyof ProfileDetailsResponse & keyof User>;

const SALES_AGENT_STRING_FIELDS = [
    ['sales_agent_id', 'agent_id'],
    ['sales_agent_name', 'name'],
    ['sales_agent_phone', 'phone_number'],
    ['sales_agent_email', 'email'],
] as const satisfies ReadonlyArray<
    readonly [keyof User, keyof NonNullable<ProfileDetailsResponse['sales_agent_details']>]
>;

const BANK_DETAIL_STRING_FIELDS = [
    ['bank_beneficiary_name', 'beneficiary_name'],
    ['bank_name', 'bank_name'],
    ['bank_account_number', 'account_number'],
    ['bank_iban', 'IBAN_number'],
    ['bank_swift_code', 'swift_code'],
] as const satisfies ReadonlyArray<
    readonly [keyof User, keyof NonNullable<ProfileDetailsResponse['bank_details']>]
>;

function applySalesAgentDetailsPatch(
    patch: Partial<User>,
    details: ProfileDetailsResponse,
): void {
    const agent = details.sales_agent_details;
    for (const [userKey, agentKey] of SALES_AGENT_STRING_FIELDS) {
        assignOrClearStringField(patch, userKey, agent?.[agentKey]);
    }
}

function applyBankDetailsPatch(
    patch: Partial<User>,
    details: ProfileDetailsResponse,
): void {
    const bank = details.bank_details;
    for (const [userKey, bankKey] of BANK_DETAIL_STRING_FIELDS) {
        assignOrClearStringField(patch, userKey, bank?.[bankKey]);
    }
}

/**
 * Map GET /v1/customers/profile-details into auth.user fields.
 * Profile-owned keys are written every call (`null` when the API sent nothing).
 * Emails are the exception: omitted keys are left out of the patch.
 */
export function mapProfileDetailsToUserPatch(
    details: ProfileDetailsResponse,
): Partial<User> {
    const patch: Partial<User> = {};

    for (const key of PROFILE_DETAIL_STRING_FIELDS) {
        assignOrClearStringField(patch, key, details[key]);
    }

    for (const key of PRESENCE_SCOPED_EMAIL_FIELDS) {
        if (key in details) {
            assignOrClearStringField(patch, key, details[key]);
        }
    }

    assignOrClearStringField(patch, 'full_name', details.full_name);
    assignOrClearStringField(patch, 'name', details.full_name);

    patch.documents = Array.isArray(details.documents) ? details.documents : null;

    patch.loan_Amount =
        parsePositiveLoanAmountAed(details.loan_Amount) ??
        parsePositiveLoanAmountAed(details.loan_amount) ??
        null;

    applySalesAgentDetailsPatch(patch, details);
    applyBankDetailsPatch(patch, details);

    return patch;
}

class ProfileService {
    /**
     * Fetch customer profile details including units and personal information
     * @returns ProfileDetailsResponse with units array and profile fields
     */
    async getProfileDetails(params?: {
        emairates_id?: string;
        type?: string;
    }): Promise<ProfileDetailsResponse> {
        const response = await apiClient.get<ProfileDetailsResponse>(
            Endpoints.profile.profileDetails,
            params ? { params } : undefined,
        );
        logger.info('ProfileService:getProfileDetails — raw response', { response });
        return response;
    }

    /**
     * Change user password
     * @param request Change password request with email, current password, and new password
     * @returns ChangePasswordResponse on success
     * @throws ChangePasswordError on failure
     */
    async changePassword(request: ChangePasswordRequest): Promise<ChangePasswordResponse> {
        return await apiClient.post<ChangePasswordResponse, ChangePasswordRequest>(
            Endpoints.profile.changePassword,
            request
        );
    }

    /**
     * Delete user account permanently
     * @returns Success response on account deletion
     */
    async deleteUser(): Promise<{ status: 'success'; message: string }> {
        return await apiClient.post<{ status: 'success'; message: string }>(
            Endpoints.profile.deleteUser
        );
    }
}

export const profileService = new ProfileService();
